import React, {PropsWithChildren, useContext, useMemo, useState} from 'react';
import {useParams} from 'react-router-dom';
import Loading from '../components/Loading/Loading';
import {useReviewSteps} from '../pages/HandoverSummaryPage/useReviewValidation';

import {useAccountContext} from './AccountContext';
import {useLazyGetSppReview} from '../pages/AccountInstructionsPage/AccountInstructionsPage';
import {BusinessUnitEnum, ReviewStatusOneviewEnum} from '../schema-types/globalTypes';
import ErrorMessage from '../components/ErrorMessage/ErrorMessage';
import {ReviewDetailsFragment} from '../schema-types/ReviewDetailsFragment';
import {SubmitHandoverContextProvider} from '../hooks/useSubmitHandover';
import validateUUID from '../utils/validateUUID';

export type ReviewContextType = ReturnType<typeof useReviewSteps> & {
  reviewDetails: ReviewDetailsFragment | undefined;
  reviewId: string;
  reviewLoading: boolean;
  isFullySubmitted: boolean;
  reviewEditable: boolean;
  isJoint: boolean;
  onNextFn?: (() => Promise<any>) | null;
  setOnNextFn: (fn: (() => Promise<any>) | null) => void;
  onSaveAndCloseFn?: (() => Promise<any>) | null;
  setOnSaveAndCloseFn: (fn: (() => Promise<any>) | null) => void;
};

const initialContext: ReviewContextType = {
  loadingReview: true,
  reviewDetails: undefined,
  stepValidation: {},
} as any;

export const ReviewContext = React.createContext<ReviewContextType>(initialContext);

export const getIsFullySubmitted = (reviewSteps: ReturnType<typeof useReviewSteps>) =>
  reviewSteps.reviewValidation?.statusOneview === ReviewStatusOneviewEnum.CLOSED ||
  reviewSteps.reviewValidation?.statusOneview === ReviewStatusOneviewEnum.COMPLETED;

const EhonsReviewContextProvider: React.FC<
  PropsWithChildren<{
    forceReviewId?: string;
    hideLoading?: boolean;
  }>
> = ({children, forceReviewId, hideLoading = false}) => {
  const [nextFn, setNextFn] = useState<(() => Promise<any>) | null>(null);
  const [saveAndCloseFn, setSaveAndCloseFn] = useState<(() => Promise<any>) | null>(null);
  const {reviewId} = useParams();
  const {account} = useAccountContext();
  const reviewIdValidated = validateUUID(reviewId);
  const review = useReviewSteps(reviewIdValidated ?? undefined);
  const [error, setError] = useState<string | null>(null);

  const isFullySubmitted = useMemo(
    () => getIsFullySubmitted(review),
    [review?.reviewValidation?.statusOneview]
  );

  const contextValue = useMemo(() => {
    const reviewNotEditable =
      review.reviewValidation?.statusOneview === ReviewStatusOneviewEnum.CLOSED ||
      review.reviewValidation?.statusOneview === ReviewStatusOneviewEnum.COMPLETED;

    const context: ReviewContextType = {
      reviewDetails: review.reviewValidation || undefined,
      ...review,
      reviewId: reviewIdValidated ?? 'noReviewId',
      reviewLoading: review.loadingReview,
      isFullySubmitted,
      reviewEditable: !reviewNotEditable,
      isJoint: review?.reviewValidation?.contact
        ? false
        : (account?.contacts?.length || 1) > 1,
      onNextFn: nextFn,
      setOnNextFn: setNextFn,
      onSaveAndCloseFn: saveAndCloseFn,
      setOnSaveAndCloseFn: setSaveAndCloseFn,
    };
    return context;
  }, [review, isFullySubmitted, account?.contacts, nextFn]);

  if (error) {
    return <ErrorMessage error={{message: error}}>{error}</ErrorMessage>;
  }
  if (!reviewId) {
    if (!review.loadingReview) {
      return <div>No review found</div>;
    }
    if (hideLoading) {
      return null;
    }
    return <Loading entityName="review" />;
  }
  if (review.loadingReview) {
    if (hideLoading) {
      return null;
    }
    return <Loading entityName="account review" />;
  }

  return (
    <ReviewContext.Provider value={contextValue}>
      <SubmitHandoverContextProvider>{children}</SubmitHandoverContextProvider>
    </ReviewContext.Provider>
  );
};

const SppDAReviewContextProvider: React.FC<
  PropsWithChildren<{
    forceReviewId?: string;
    hideLoading?: boolean;
  }>
> = ({children, forceReviewId, hideLoading = false}) => {
  const {
    review,
    reviewId,

    loading: reviewLoading,
    called: reviewCalled,
    error,
  } = useLazyGetSppReview();
  const reviewSteps = useReviewSteps(reviewId);

  const isFullySubmitted = useMemo(
    () => getIsFullySubmitted(reviewSteps),
    [reviewSteps.reviewValidation?.statusOneview]
  );

  const contextValue = useMemo(() => {
    const context: ReviewContextType = {
      reviewDetails: review || undefined,
      ...reviewSteps,
      reviewId,
      reviewLoading,
      isFullySubmitted,
      reviewEditable: true,
      isJoint: false,
      setOnNextFn: () => {},
      onNextFn: null,
      setOnSaveAndCloseFn: () => {},
      onSaveAndCloseFn: null,
    };
    return context;
  }, [
    {
      review,
      reviewSteps,
      reviewId,
      reviewLoading,
      isFullySubmitted,
    },
  ]);

  if (!reviewId) {
    if (!reviewCalled) {
      return <Loading />;
    }
    if (!reviewLoading) {
      return <div>No review found...</div>;
    }
    if (hideLoading) {
      return null;
    }
    return <Loading />;
  }
  if (reviewSteps.loadingReview) {
    if (hideLoading) {
      return null;
    }
    return <Loading />;
  }
  if (reviewSteps.error) {
    return <div>An error occured</div>;
  }
  return (
    <ReviewContext.Provider value={contextValue}>
      <SubmitHandoverContextProvider>{children}</SubmitHandoverContextProvider>
    </ReviewContext.Provider>
  );
};

export const ReviewContextProvider: React.FC<
  PropsWithChildren<{
    forceReviewId?: string;
    hideLoading?: boolean;
  }>
> = (props) => {
  const {accountType} = useAccountContext();
  if (accountType === BusinessUnitEnum.DA) {
    return <SppDAReviewContextProvider {...props} />;
  }
  if (accountType === BusinessUnitEnum.SFP || accountType === BusinessUnitEnum.AR) {
    return <EhonsReviewContextProvider {...props} />;
  }
  return <Loading />;
};

export const useReviewContext = () => {
  const context = useContext(ReviewContext);
  return context;
};
